import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Play, ListPlus, Loader2, Music, Search, X, ScrollText } from 'lucide-react';
import LyricsModal from '../../components/LyricsModal';
import PublicLayout from '../../components/public/PublicLayout';
import { useMusicPlayer } from '../../contexts/MusicPlayerContext';
import SEO from '../../components/SEO';
import { PUBLIC_BASE } from './config';
import { GENRES } from '../../data/genres';

const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

function PublicLatestSongsPage() {
  const navigate = useNavigate();
  const { playImmediate, addBulkToPlaylist } = useMusicPlayer();

  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [playingId, setPlayingId] = useState(null);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [genres, setGenres] = useState(GENRES);
  const [bgVideo, setBgVideo] = useState(null);

  const debounceRef = useRef(null);

  const fetchSongs = useCallback(async (p, q, genre, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const params = { page: p, limit: 30, search: q };
      if (genre) params.genreId = genre;
      const res = await axios.get('/public/songs', { params });
      const items = res.data.items || [];
      setSongs((prev) => (append ? [...prev, ...items] : items));
      setPage(res.data.currentPage || p);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      console.error('최신 곡 목록 로드 실패:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // 배경 뮤직비디오 랜덤 선택
  useEffect(() => {
    // 배경: 자체 S3 모션 루프 (유튜브 임베드 차단·로딩 지연 원천 회피)
    axios.get('/public/bg-loops')
      .then((res) => {
        const loops = res.data.items || [];
        if (loops.length > 0) {
          setBgVideo(loops[Math.floor(Math.random() * loops.length)]);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    axios.get('/public/genre-counts')
      .then((res) => {
        const counts = {};
        (res.data || []).forEach((r) => { counts[r.genreId] = Number(r.cnt); });
        setGenres([...GENRES].sort((a, b) => (counts[b.id] || 0) - (counts[a.id] || 0)));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchSongs(1, search, selectedGenre);
  }, [search, selectedGenre]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearch(val), 400);
  };

  const clearSearch = () => {
    setInputValue('');
    setSearch('');
  };

  const handleGenreClick = (id) => {
    setSelectedGenre((prev) => (prev === id ? null : id));
  };

  const [lyricsModal, setLyricsModal] = useState(null); // { lyrics, syncLyrics, songName, artistName }

  const buildTrack = (song) => ({
    id: song.id,
    nameKr: song.nameKr,
    artistKr: song.artistKr || song.album?.artist?.nameKr || '',
    fileUrl: song.fileUrl,
    albumImage: song.album?.image || FALLBACK_IMG,
    albumName: song.album?.nameKr,
    hasLyrics: song.hasLyrics,
    hasSyncLyrics: song.hasSyncLyrics,
  });

  const handlePlay = (song) => {
    setPlayingId(song.id);
    playImmediate([buildTrack(song)]);
  };

  const handleAdd = (e, song) => {
    e.stopPropagation();
    addBulkToPlaylist([buildTrack(song)]);
  };

  const handleLoadMore = () => {
    if (page < totalPages && !loadingMore) {
      fetchSongs(page + 1, search, selectedGenre, true);
    }
  };

  const formatDate = (d) => {
    if (!d) return '';
    const [y, m, day] = d.split('T')[0].split('-');
    return `${y}.${m}.${day}`;
  };

  return (
    <PublicLayout bgClassName="bg-transparent">
      <SEO
        title="최신음악"
        description="p!nk에서 유통중인 최신 음악을 확인하세요."
        path="/songs"
      />

      {/* 배경 뮤직비디오 */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        {/* 기본 배경색 (영상 로딩 전 / 미지원 환경 fallback) */}
        <div className="absolute inset-0 bg-[#0d1526]" />
        {bgVideo && (
          <video
            src={bgVideo.motionLoopUrl}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setBgVideo(null)}
          />
        )}
        {/* 사이트 톤 유지 오버레이 */}
        <div className="absolute inset-0 bg-[#0d1526]/90" />
      </div>

      {/* 콘텐츠 (배경 영상 위) */}
      <div className="relative z-10">

      {/* 헤더 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 pb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight text-center">최신음악</h1>
      </div>

      {/* 장르 필터 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-3">
        <div className="overflow-x-auto scroll-dark">
          <div className="flex gap-2 pb-1 w-max mx-auto">
            {genres.map((g) => (
              <button
                key={g.id}
                onClick={() => handleGenreClick(g.id)}
                className={`shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  selectedGenre === g.id
                    ? 'bg-sky-500 border-sky-500 text-white'
                    : 'bg-white/5 border-white/10 text-slate-400 hover:text-white hover:border-white/20'
                }`}>
                {g.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 검색 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-4">
        <div className="flex items-center sm:justify-end justify-center">
          <div className="relative w-full sm:w-72">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              placeholder="곡명 · 아티스트 검색"
              className="w-full pl-8 pr-8 py-2 bg-white/5 border border-white/10 rounded-full text-sm text-white placeholder-slate-500 focus:outline-none focus:border-sky-500/50 transition-colors"
            />
            {inputValue && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                <X size={13} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* 곡 목록 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-10">
        {loading ? (
          <div className="flex items-center justify-center py-20 gap-3 text-slate-400">
            <Loader2 size={20} className="animate-spin" />
            <span>로딩 중...</span>
          </div>
        ) : songs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-500">
            <Music size={40} />
            <p>{search ? '검색 결과가 없습니다.' : '곡이 없습니다.'}</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-white/5">
              {songs.map((song, idx) => (
                <div
                  key={song.id}
                  onClick={() => handlePlay(song)}
                  className={`group flex items-center gap-3 py-2.5 px-2 rounded-lg cursor-pointer transition-colors hover:bg-white/5 ${
                    playingId === song.id ? 'bg-white/5' : ''
                  }`}>
                  {/* 순번 / 재생 아이콘 */}
                  <div className="w-7 text-center shrink-0">
                    <span className={`text-slate-500 text-xs group-hover:hidden ${playingId === song.id ? 'hidden' : ''}`}>
                      {idx + 1}
                    </span>
                    <Play
                      size={13}
                      className={`text-sky-400 mx-auto hidden group-hover:block ${playingId === song.id ? '!block' : ''}`}
                      fill="currentColor"
                    />
                  </div>

                  {/* 앨범 이미지 */}
                  <img
                    src={song.album?.image || FALLBACK_IMG}
                    alt={song.album?.nameKr}
                    className="w-10 h-10 rounded-lg object-cover shrink-0"
                    onClick={(e) => { e.stopPropagation(); song.album?.id && navigate(`${PUBLIC_BASE}/albums/${song.album.id}`); }}
                  />

                  {/* 곡명 + 아티스트 */}
                  <div className="flex-1 min-w-0">
                    <span
                      className={`text-sm font-medium truncate block ${
                        playingId === song.id ? 'text-sky-400' : 'text-white group-hover:text-sky-300'
                      }`}>
                      {song.nameKr}
                    </span>
                    <p
                      className="text-xs text-slate-500 truncate mt-0.5 hover:text-sky-400 cursor-pointer w-fit"
                      onClick={(e) => {
                        e.stopPropagation();
                        const artistId = song.album?.artist?.id;
                        if (artistId) navigate(`${PUBLIC_BASE}/artists/${artistId}`);
                      }}>
                      {song.artistKr || song.album?.artist?.nameKr}
                    </p>
                  </div>

                  {/* 앨범명 (md 이상) */}
                  <p
                    className="hidden md:block text-xs text-slate-500 truncate max-w-[180px] hover:text-sky-400 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (song.album?.id) navigate(`${PUBLIC_BASE}/albums/${song.album.id}`);
                    }}>
                    {song.album?.nameKr}
                  </p>

                  {/* 발매일 (lg 이상) */}
                  <span className="hidden lg:block text-xs text-slate-600 shrink-0 w-14 text-right">
                    {formatDate(song.album?.releaseDateTime)}
                  </span>

                  {/* 가사 */}
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (song.hasLyrics) {
                        try {
                          const lyricsRes = await axios.get(`/public/songs/${song.id}/lyrics`);
                          setLyricsModal({
                            songId: song.id,
                            lyrics: lyricsRes.data.lyrics,
                            syncLyrics: lyricsRes.data.syncLyrics,
                            songName: song.nameKr,
                            artistName: song.artistKr || song.album?.artist?.nameKr || '',
                          });
                        } catch (err) {
                          console.error('가사 로드 실패:', err);
                        }
                      }
                    }}
                    title="가사"
                    disabled={!song.hasLyrics}
                    className={`shrink-0 p-1.5 rounded-lg transition-all ${
                      song.hasSyncLyrics
                        ? 'text-sky-400 hover:text-sky-300 hover:bg-white/5'
                        : song.hasLyrics
                        ? 'text-slate-500 hover:text-slate-300 hover:bg-white/5'
                        : 'text-slate-700 cursor-default'
                    }`}>
                    <ScrollText size={15} />
                  </button>

                  {/* 담기 */}
                  <button
                    onClick={(e) => handleAdd(e, song)}
                    title="담기"
                    className="shrink-0 p-1.5 text-slate-500 hover:text-sky-400 transition-all rounded-lg hover:bg-white/5">
                    <ListPlus size={16} />
                  </button>
                </div>
              ))}
            </div>

            {/* 더 보기 */}
            {page < totalPages && (
              <div className="flex justify-center mt-8">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="flex items-center gap-2 px-6 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-slate-300 text-sm transition-colors">
                  {loadingMore ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      로딩 중...
                    </>
                  ) : (
                    '더 보기'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      </div> {/* relative z-10 닫기 */}

      {/* 가사 모달 */}
      {lyricsModal && (
        <LyricsModal
          {...lyricsModal}
          onClose={() => setLyricsModal(null)}
        />
      )}
    </PublicLayout>
  );
}

export default PublicLatestSongsPage;
